/** Port von `Pages/Auth/AuthApps.razor`. */

import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { isDemoMode } from '@/auth/demoAccount';
import { getAuthenticatedAccount, msalInstance } from '@/auth/msalInstance';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui';
import { API_SCOPES } from '@/config/authConfig';
import type { App } from '@/types/entities';

export function AppSelection(): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    /** Pendant zu `OnInitializedAsync`: Token für den API-Scope sichern, dann Apps laden. */
    const prepare = async (): Promise<void> => {
      // Im Demobetrieb gibt es kein Token zu sichern; der Zweig würde interaktiv
      // authentifizieren und damit die Anmeldeumgehung aushebeln.
      if (!isDemoMode) {
        const account = getAuthenticatedAccount();
        try {
          if (account === null) {
            throw new Error('Kein angemeldetes Konto.');
          }
          await msalInstance.acquireTokenSilent({ scopes: API_SCOPES, account });
        } catch {
          // Ohne Token wird — wie über `InteractiveRequestUrl` im Altclient — erneut
          // interaktiv authentifiziert.
          await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES });
          return;
        }
      }

      const apps = auth.apps === null || auth.apps.length === 0 ? await auth.loadApps() : auth.apps;
      if (apps.length === 0) {
        void navigate('/authentication/error/no-apps');
      }
    };

    // `AuthApps.razor` protokolliert Fehler nur auf der Konsole und lässt die Seite stehen.
    void prepare().catch(() => undefined);
  }, [auth, navigate]);

  const onAppSelected = async (app: App): Promise<void> => {
    auth.setCurrentApp(app);
    await auth.login();
    void navigate('/');
  };

  return (
    <div>
      <span>Bitte wählen Sie eine Applikation.</span>
      <div className="iwm-app-selection" style={{ marginTop: '1rem' }}>
        {auth.apps?.map((app) => (
          <div
            key={app.URI}
            className="iwm-card iwm-app"
            onClick={() => {
              void onAppSelected(app);
            }}
          >
            {app.Name}
          </div>
        ))}
      </div>
      <div style={{ marginTop: '1rem' }}>
        <Button
          primary
          onClick={() => {
            void navigate('/authentication/logout');
          }}
        >
          Ausloggen
        </Button>
      </div>
    </div>
  );
}

export default AppSelection;
